import { formatNZDate } from "../../utils/timezone";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import DatePicker from "../../components/DatePicker";
import { CheckCircle, Calendar as CalendarIcon, AlertCircle } from "lucide-react";
import { cn } from "../../lib/utils";

export function ShopOrderDialogs({
  confirmAvailabilityDialogOpen,
  setConfirmAvailabilityDialogOpen,
  confirmJobDialogOpen,
  setConfirmJobDialogOpen,
  assignDateDialogOpen,
  setAssignDateDialogOpen,
  completionDialogOpen,
  setCompletionDialogOpen,
  pauseDialogOpen,
  setPauseDialogOpen,
  selectedOrder,
  setSelectedOrder,
  selectedOrderForAction,
  availabilityForm,
  setAvailabilityForm,
  serviceDateForm,
  setServiceDateForm,
  completionForm,
  setCompletionForm,
  pauseReason,
  setPauseReason,
  submittingAvailability,
  submittingPause,
  confirmingJob,
  assigningDate,
  markingComplete,
  handleConfirmAvailability,
  handleConfirmJob,
  handleAssignServiceDate,
  handleCompleteWithDetails,
  handleRequestPause,
}) {
  return (
    <>
      <Dialog open={confirmAvailabilityDialogOpen} onOpenChange={(open) => {
        setConfirmAvailabilityDialogOpen(open);
        if (!open) {
          setSelectedOrder(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Confirm Availability
            </DialogTitle>
            <DialogDescription>
              Order #{selectedOrder?.order_id?.substring(0, 8)} - ${(selectedOrder?.shop_payout || 0).toFixed(2)} payout
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Stock Confirmation */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Stock Availability</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={availabilityForm.stock_confirmed ? "default" : "outline"}
                  className={cn(
                    "h-auto py-4 flex flex-col items-center gap-2",
                    availabilityForm.stock_confirmed && "bg-green-600 hover:bg-green-700"
                  )}
                  onClick={() => setAvailabilityForm(prev => ({ ...prev, stock_confirmed: true }))}
                >
                  <CheckCircle className="h-6 w-6" />
                  <span>In Stock</span>
                </Button>
                <Button
                  type="button"
                  variant={!availabilityForm.stock_confirmed ? "default" : "outline"}
                  className={cn(
                    "h-auto py-4 flex flex-col items-center gap-2",
                    !availabilityForm.stock_confirmed && "bg-red-600 hover:bg-red-700"
                  )}
                  onClick={() => setAvailabilityForm(prev => ({ ...prev, stock_confirmed: false }))}
                >
                  <AlertCircle className="h-6 w-6" />
                  <span>Not Available</span>
                </Button>
              </div>
            </div>

            {availabilityForm.stock_confirmed ? (
              <>
                {/* Service Date */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Proposed Service Date *</Label>
                  <DatePicker
                    value={availabilityForm.proposed_service_date}
                    onChange={(date) => setAvailabilityForm(prev => ({ ...prev, proposed_service_date: date }))}
                    placeholder="Select earliest fitment date"
                    minDate={new Date()}
                  />
                </div>

                {/* Fitment Time */}
                <div className="space-y-2">
                  <Label>Preferred Time (Optional)</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                    value={availabilityForm.proposed_service_time}
                    onChange={(e) => setAvailabilityForm(prev => ({ ...prev, proposed_service_time: e.target.value }))}
                  >
                    <option value="">Any time</option>
                    <option value="8:00 AM">8:00 AM</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                  </select>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes for Customer (Optional)</Label>
                  <Textarea
                    placeholder="Any additional information about the appointment..."
                    value={availabilityForm.notes}
                    onChange={(e) => setAvailabilityForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                  />
                </div>
              </>
            ) : (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-600">
                  By marking as "Not Available", this order will be cancelled and the customer will be notified to select another quote.
                </p>
                <div className="mt-3 space-y-2">
                  <Label>Reason (Optional)</Label>
                  <Textarea
                    placeholder="Explain why the stock is not available..."
                    value={availabilityForm.notes}
                    onChange={(e) => setAvailabilityForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmAvailabilityDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAvailability}
              disabled={submittingAvailability}
              className={availabilityForm.stock_confirmed ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
            >
              {submittingAvailability 
                ? "Submitting..." 
                : availabilityForm.stock_confirmed 
                  ? "Confirm & Notify Customer" 
                  : "Cancel Order"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Job Dialog */}
      <Dialog open={confirmJobDialogOpen} onOpenChange={setConfirmJobDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              Confirm Job
            </DialogTitle>
            <DialogDescription>
              By confirming this job, your contact details will be shared with the customer so they can arrange a booking time with you directly.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Order: <span className="font-medium">#{selectedOrderForAction?.order_id?.substring(0, 8)}</span>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Amount: <span className="font-medium text-primary">${(selectedOrderForAction?.shop_payout || selectedOrderForAction?.order_amount || 0).toFixed(2)}</span>
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setConfirmJobDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmJob}
              disabled={confirmingJob}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {confirmingJob ? "Confirming..." : "Confirm Job"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Service Date Dialog */}
      <Dialog open={assignDateDialogOpen} onOpenChange={setAssignDateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              Assign Service Date
            </DialogTitle>
            <DialogDescription>
              Set the agreed date and time for this service. This helps with job tracking and record-keeping.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Service Date *</Label>
              <DatePicker
                value={serviceDateForm.service_date}
                onChange={(date) => setServiceDateForm(prev => ({ ...prev, service_date: date }))}
                placeholder="Select service date"
                minDate={new Date()}
              />
            </div>
            <div className="space-y-2">
              <Label>Service Time (Optional)</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={serviceDateForm.service_time}
                onChange={(e) => setServiceDateForm(prev => ({ ...prev, service_time: e.target.value }))}
              >
                <option value="">Any time</option>
                <option value="8:00 AM">8:00 AM</option>
                <option value="9:00 AM">9:00 AM</option>
                <option value="10:00 AM">10:00 AM</option>
                <option value="11:00 AM">11:00 AM</option>
                <option value="12:00 PM">12:00 PM</option>
                <option value="1:00 PM">1:00 PM</option>
                <option value="2:00 PM">2:00 PM</option>
                <option value="3:00 PM">3:00 PM</option>
                <option value="4:00 PM">4:00 PM</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setAssignDateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignServiceDate}
              disabled={assigningDate || !serviceDateForm.service_date}
            >
              {assigningDate ? "Saving..." : "Assign Date"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Completion Details Dialog */}
      <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Complete Service
            </DialogTitle>
            <DialogDescription>
              Enter the service completion details. This will send a confirmation and review request to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Service Date *</Label>
                <Input
                  type="date"
                  value={completionForm.service_date}
                  onChange={(e) => setCompletionForm(prev => ({ ...prev, service_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Service Time (Optional)</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={completionForm.service_time}
                  onChange={(e) => setCompletionForm(prev => ({ ...prev, service_time: e.target.value }))}
                >
                  <option value="">Select time</option>
                  <option value="8:00 AM">8:00 AM</option>
                  <option value="9:00 AM">9:00 AM</option>
                  <option value="10:00 AM">10:00 AM</option>
                  <option value="11:00 AM">11:00 AM</option>
                  <option value="12:00 PM">12:00 PM</option>
                  <option value="1:00 PM">1:00 PM</option>
                  <option value="2:00 PM">2:00 PM</option>
                  <option value="3:00 PM">3:00 PM</option>
                  <option value="4:00 PM">4:00 PM</option>
                  <option value="5:00 PM">5:00 PM</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={completionForm.payment_method}
                onChange={(e) => setCompletionForm(prev => ({ ...prev, payment_method: e.target.value }))}
                required
              >
                <option value="">Select payment method</option>
                <option value="Cash">Cash</option>
                <option value="EFTPOS">EFTPOS</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Afterpay">Afterpay</option>
                <option value="Laybuy">Laybuy</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Completion Notes (Optional)</Label>
              <Textarea
                placeholder="Any additional notes about the service..."
                value={completionForm.completion_notes}
                onChange={(e) => setCompletionForm(prev => ({ ...prev, completion_notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setCompletionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCompleteWithDetails}
              disabled={markingComplete || !completionForm.service_date || !completionForm.payment_method}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {markingComplete ? "Completing..." : "Mark as Completed"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pause Account Dialog */}
      <Dialog open={pauseDialogOpen} onOpenChange={setPauseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pause Your Account</DialogTitle>
            <DialogDescription>
              Pausing your account will stop all incoming quote requests. You can request to resume at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for Pausing (Required)</Label>
              <Textarea
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="e.g., Going on holiday, temporarily closed for renovations..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">Minimum 10 characters</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRequestPause}
              disabled={submittingPause || pauseReason.length < 10}
            >
              {submittingPause ? "Pausing..." : "Pause Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
